/**
 * Phase 14.5 Step 3 — TrackQualityMarkov sub-form.
 *
 * Registry-driven from `track_quality.fields`:
 *   transition_matrix       — 4×4 row-stochastic
 *   hold_time_means_s       — tuple4 (LOCKED, COASTING, LOST, REACQUIRING)
 *   reacquisition_probability_per_tick — scalar (0..1)
 *   tick_rate_hz            — scalar (0..10000)
 *   seed                    — int
 *
 * Render shows the 4×4 matrix as a labelled grid with per-row sum
 * readout (advisory; the backend Pydantic enforces row-stochasticity
 * with tolerance). State labels come from `registry.track_quality_states`.
 *
 * Held to the Step-2 invariant: no internal `useForm`.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ROW_TOL = 1e-3;

function _setField(value, field, raw, asInt, onChange) {
    const parsed = asInt ? parseInt(raw || "0", 10) : parseFloat(raw || "0");
    onChange?.({ ...(value || {}), [field]: Number.isFinite(parsed) ? parsed : 0 });
}

function _setTuple4(value, field, idx, raw, onChange) {
    const arr = Array.isArray(value?.[field]) ? [...value[field]] : [0, 0, 0, 0];
    arr[idx] = parseFloat(raw);
    if (!Number.isFinite(arr[idx])) arr[idx] = 0;
    onChange?.({ ...(value || {}), [field]: arr });
}

function _setMatrixCell(value, row, col, raw, onChange) {
    const m = (value?.transition_matrix || [[1, 0, 0, 0], [0, 1, 0, 0], [0, 0, 1, 0], [0, 0, 0, 1]])
        .map((r) => [...r]);
    m[row][col] = parseFloat(raw);
    if (!Number.isFinite(m[row][col])) m[row][col] = 0;
    onChange?.({ ...(value || {}), transition_matrix: m });
}

export default function TrackQualityMarkovSubForm({ value, onChange, registry, states }) {
    const stateLabels = states && states.length === 4
        ? states
        : ["LOCKED", "COASTING", "LOST", "REACQUIRING"];
    const matrix = value?.transition_matrix || [[0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0]];
    const holdTimes = value?.hold_time_means_s || [0, 0, 0, 0];

    const fields = Array.isArray(registry?.fields) ? registry.fields : [];
    const byId = Object.fromEntries(fields.map((f) => [f.id, f]));

    return (
        <div className="space-y-4" data-testid="track-quality-subform">
            <div data-testid="track-quality-matrix">
                <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                    transition_matrix <span className="text-ux-text-3/60">(row-stochastic 4×4)</span>
                </Label>
                <div className="mt-2 grid grid-cols-[auto_repeat(4,1fr)_auto] items-center gap-2">
                    <span />
                    {stateLabels.map((s) => (
                        <span key={`col-${s}`} className="text-[10px] text-ux-text-3 text-center">{s}</span>
                    ))}
                    <span className="text-[10px] text-ux-text-3 text-right">Σ</span>
                    {stateLabels.map((rowState, r) => {
                        const rowSum = (matrix[r] || []).reduce((s, x) => s + (Number(x) || 0), 0);
                        const offRow = Math.abs(rowSum - 1) > ROW_TOL;
                        return (
                            <>
                                <span
                                    key={`row-label-${rowState}`}
                                    className="text-[10px] text-ux-text-3"
                                >
                                    {rowState}
                                </span>
                                {[0, 1, 2, 3].map((c) => (
                                    <Input
                                        key={`cell-${r}-${c}`}
                                        type="number"
                                        step="any"
                                        value={(matrix[r] || [])[c] ?? 0}
                                        onChange={(e) => _setMatrixCell(value, r, c, e.target.value, onChange)}
                                        data-testid={`track-quality-cell-${r}-${c}`}
                                        aria-label={`P(${rowState}→${stateLabels[c]})`}
                                    />
                                ))}
                                <span
                                    key={`row-sum-${r}`}
                                    className={[
                                        "text-[11px] text-right tabular-nums",
                                        offRow ? "text-amber-300" : "text-ux-text-3",
                                    ].join(" ")}
                                    data-testid={`track-quality-row-sum-${r}`}
                                    data-off-row={offRow ? "1" : "0"}
                                >
                                    {rowSum.toFixed(3)}
                                </span>
                            </>
                        );
                    })}
                </div>
            </div>

            <div>
                <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                    hold_time_means_s <span className="text-ux-text-3/60">(s)</span>
                </Label>
                <div className="mt-1 grid grid-cols-4 gap-2">
                    {[0, 1, 2, 3].map((i) => (
                        <Input
                            key={`hold-${i}`}
                            type="number"
                            step="any"
                            min="0"
                            value={holdTimes[i] ?? 0}
                            onChange={(e) => _setTuple4(value, "hold_time_means_s", i, e.target.value, onChange)}
                            data-testid={`track-quality-hold-${i}`}
                            aria-label={`hold ${stateLabels[i]}`}
                        />
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                {byId.reacquisition_probability_per_tick && (
                    <div>
                        <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            reacq prob / tick
                        </Label>
                        <Input
                            type="number"
                            step="any"
                            min="0"
                            max="1"
                            value={value?.reacquisition_probability_per_tick ?? 0}
                            onChange={(e) => _setField(value, "reacquisition_probability_per_tick", e.target.value, false, onChange)}
                            data-testid="track-quality-reacq-prob"
                        />
                    </div>
                )}
                {byId.tick_rate_hz && (
                    <div>
                        <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            tick_rate_hz
                        </Label>
                        <Input
                            type="number"
                            step="any"
                            min="0"
                            max="10000"
                            value={value?.tick_rate_hz ?? 0}
                            onChange={(e) => _setField(value, "tick_rate_hz", e.target.value, false, onChange)}
                            data-testid="track-quality-tick-rate"
                        />
                    </div>
                )}
                {byId.seed && (
                    <div>
                        <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            seed
                        </Label>
                        <Input
                            type="number"
                            step="1"
                            value={value?.seed ?? 0}
                            onChange={(e) => _setField(value, "seed", e.target.value, true, onChange)}
                            data-testid="track-quality-seed"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
